'use client';

import logError from '@/helpers/logError';
import Image, { ImageProps } from 'next/image';
import QRCoder from 'qrcode';
import { useEffect, useState } from 'react';
import FakeQR from './fake-qr.png';

export interface QRCodeProps
	extends Omit<ImageProps, 'src' | 'alt' | 'width' | 'height'> {
	testId?: string;
	data: string;
	alt: string;
	width?: number;
	height?: number;
}

const generateQRCode = async (
	enc: string,
	width?: number,
): Promise<string | null> => {
	if (enc.length > 2048) {
		logError(new Error('QR code data too long for url encoding.'));
		return null;
	}

	try {
		// This is the QR code dataurl.
		return await QRCoder.toDataURL(enc, { width });
	} catch (error) {
		return null;
	}
};

const QRCode = (props: QRCodeProps) => {
	const {
		testId = 'QRCode',
		data,
		alt,
		width = 180,
		height = 180,
		...otherProps
	} = props;
	const [dataurl, setDataurl] = useState<string | null>(null);

	useEffect(() => {
		const make = async () => {
			try {
				const url = await generateQRCode(data, width);
				setDataurl(url);
			} catch (error) {
				if (error instanceof Error) {
					logError(error);
				} else {
					logError(new Error('Failed to generate QR code'));
				}
			}
		};
		make();
	}, [data, width]);

	if (!dataurl) {
		return (
			<div data-testid={testId} className="aspect-square overflow-clip blur-sm">
				<Image
					src={FakeQR}
					alt={'Generating QR code...'}
					width={width}
					height={height}
					{...otherProps}
				/>
				<p className="sr-only">Generating QR code...</p>
			</div>
		);
	}

	return (
		<Image
			src={dataurl}
			alt={alt}
			data-testid={testId}
			width={width}
			height={height}
			{...otherProps}
		/>
	);
};

export default QRCode;
